import React, { useRef, useEffect, useCallback, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import {
  Row,
  Col,
  Card,
  CardBody,
  Container,
  Spinner,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
} from 'reactstrap';
import { Form } from '@availity/form';
import EmailEditor from 'react-email-editor';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import toastr from 'toastr';
import '@availity/yup';

import * as demTemplatesActions from '../../store/demTemplates/actions';
import {
  selectSelectedDemTemplate,
  selectDemTemplatesLoading,
  selectAiPanelOpen,
  selectAiMessages,
  selectAiLoading,
  selectAiError,
} from '../../selectors/demTemplate';
import Breadcrumbs from '../../components/Common/Breadcrumb';
import FormTemplateInfo from '../../components/DemTemplates/FormTemplateInfo';
import MacroInfoPanel from '../../components/DemTemplates/MacroInfoPanel';
import AiChatPanel from '../../components/DemTemplates/AiChatPanel';
import formValidation from '../../components/DemTemplates/formValidation';

const EMAIL_EDITOR_OPTIONS = {
  features: {
    userUploads: true,
  },
};

const Editor = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const isEditMode = !!id;
  const history = useHistory();
  const dispatch = useDispatch();
  const emailEditorRef = useRef(null);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [importJson, setImportJson] = useState('');
  const [importError, setImportError] = useState('');
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [exportJson, setExportJson] = useState('');

  const loading = useSelector(selectDemTemplatesLoading);
  const selectedTemplate = useSelector(selectSelectedDemTemplate);
  const isAiPanelOpen = useSelector(selectAiPanelOpen);
  const admin = useSelector((state) => state.Auth.admin);
  const canUseAi = ['ADMIN', 'SUPER_ADMIN'].includes(admin?.role);

  const loadTemplateDesign = useCallback(() => {
    if (
      !isEditMode ||
      !selectedTemplate?.design ||
      !emailEditorRef.current?.editor
    )
      return;
    emailEditorRef.current.editor.loadDesign(selectedTemplate.design);
  }, [isEditMode, selectedTemplate]);

  useEffect(() => {
    if (isEditMode) {
      dispatch(demTemplatesActions.fetchDemTemplate(id));
    }
    return () => dispatch(demTemplatesActions.resetState());
  }, [dispatch, id, isEditMode]);

  // Load saved design when template arrives from Firestore (handles race condition:
  // editor ready may fire before or after Firebase response)
  useEffect(() => {
    loadTemplateDesign();
  }, [loadTemplateDesign]);

  // Register image upload callback once editor is ready
  const onEditorReady = useCallback(() => {
    loadTemplateDesign();
  }, [loadTemplateDesign]);

  const handleSave = (formValues) => {
    if (!emailEditorRef.current) return;

    emailEditorRef.current.editor.exportHtml(({ design, html }) => {
      const templateData = {
        ...formValues,
        design,
        html,
        ...(isEditMode && { templateId: id }),
      };

      dispatch(
        isEditMode
          ? demTemplatesActions.updateDemTemplate(templateData)
          : demTemplatesActions.createDemTemplate(templateData),
      );

      history.push('/demTemplates/list');
    });
  };

  const handlePreview = () => {
    if (!emailEditorRef.current) return;
    emailEditorRef.current.editor.exportHtml(({ html }) => {
      const blob = new Blob([html], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
    });
  };

  const handleOpenImportModal = () => {
    setImportJson('');
    setImportError('');
    setIsImportModalOpen(true);
  };

  const handleCloseImportModal = () => {
    setIsImportModalOpen(false);
    setImportError('');
  };

  const handleImportDesign = () => {
    if (!emailEditorRef.current?.editor) {
      setImportError(t('Editor is not ready. Try again in a few seconds.'));
      return;
    }

    const trimmedJson = importJson.trim();
    if (!trimmedJson) {
      setImportError(t('Paste valid JSON before importing.'));
      return;
    }

    try {
      const parsedJson = JSON.parse(trimmedJson);
      const designToLoad = parsedJson?.design ?? parsedJson;

      if (
        !designToLoad ||
        typeof designToLoad !== 'object' ||
        Array.isArray(designToLoad)
      ) {
        setImportError(
          t(
            "Invalid JSON for Unlayer. Paste a design JSON or an object with a 'design' property.",
          ),
        );
        return;
      }

      emailEditorRef.current.editor.loadDesign(designToLoad);
      handleCloseImportModal();
      toastr.success(t('Template imported successfully'), '');
    } catch (error) {
      setImportError(t('Invalid JSON. Check the syntax and try again.'));
    }
  };

  const handleOpenExportModal = () => {
    if (!emailEditorRef.current?.editor) {
      toastr.error(t('Editor is not ready. Try again in a few seconds.'), '');
      return;
    }

    emailEditorRef.current.editor.exportHtml(({ design }) => {
      if (!design || typeof design !== 'object') {
        toastr.error(t('No design available to export.'), '');
        return;
      }

      setExportJson(JSON.stringify(design, null, 2));
      setIsExportModalOpen(true);
    });
  };

  const handleCloseExportModal = () => {
    setIsExportModalOpen(false);
  };

  const handleCopyExportJson = async () => {
    try {
      await navigator.clipboard.writeText(exportJson);
      toastr.success(t('JSON copied to clipboard'), '');
    } catch (error) {
      toastr.error(t('Automatic copy not available. Copy from the text area.'), '');
    }
  };

  const initialValues = {
    name: selectedTemplate?.name || '',
    description: selectedTemplate?.description || '',
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs
            title={isEditMode ? t('Edit DEM Template') : t('New DEM Template')}
          />
          <Form
            initialValues={initialValues}
            enableReinitialize
            validationSchema={formValidation}
            onSubmit={handleSave}
          >
            {({ isValid }) => (
              <>
                <Row className="mb-3 align-items-center">
                  <Col>
                    <Button
                      color="secondary"
                      outline
                      size="sm"
                      onClick={() => history.push('/demTemplates/list')}
                    >
                      <i className="bx bx-arrow-back mr-1" />
                      {t('Back to list')}
                    </Button>
                  </Col>
                  <Col className="text-right">
                    <Button
                      color="info"
                      outline
                      size="sm"
                      className="mr-2"
                      type="button"
                      onClick={handlePreview}
                    >
                      <i className="bx bx-show mr-1" />
                      {t('Preview')}
                    </Button>
                    <Button
                      color="secondary"
                      outline
                      size="sm"
                      className="mr-2"
                      type="button"
                      onClick={handleOpenImportModal}
                    >
                      <i className="bx bx-import mr-1" />
                      {t('Import JSON')}
                    </Button>
                    <Button
                      color="secondary"
                      outline
                      size="sm"
                      className="mr-2"
                      type="button"
                      onClick={handleOpenExportModal}
                    >
                      <i className="bx bx-export mr-1" />
                      {t('Export JSON')}
                    </Button>
                    {canUseAi && (
                      <Button
                        color="primary"
                        outline={!isAiPanelOpen}
                        size="sm"
                        className="mr-2"
                        type="button"
                        onClick={() =>
                          dispatch(demTemplatesActions.toggleAiPanel())
                        }
                      >
                        <i className="bx bx-bot mr-1" />
                        {t('AI Assistant')}
                      </Button>
                    )}
                    <Button
                      color="primary"
                      size="sm"
                      type="submit"
                      disabled={!isValid || loading}
                    >
                      {loading ? (
                        <Spinner size="sm" color="light" />
                      ) : (
                        <i className="bx bx-save mr-1" />
                      )}
                      {t('Save Template')}
                    </Button>
                  </Col>
                </Row>

                <Row>
                  <Col md="3">
                    <Card>
                      <CardBody>
                        <FormTemplateInfo />
                        <MacroInfoPanel />
                      </CardBody>
                    </Card>
                  </Col>
                  <Col md={isAiPanelOpen ? '6' : '9'}>
                    <Card className="p-0" style={{ overflow: 'hidden' }}>
                      <EmailEditor
                        ref={emailEditorRef}
                        onReady={onEditorReady}
                        minHeight="80vh"
                        options={EMAIL_EDITOR_OPTIONS}
                      />
                    </Card>
                  </Col>
                  {canUseAi && isAiPanelOpen && (
                    <Col md="3">
                      <AiChatPanel
                        emailEditorRef={emailEditorRef}
                        messagesSelector={selectAiMessages}
                        loadingSelector={selectAiLoading}
                        errorSelector={selectAiError}
                        onSend={(prompt, currentDesign) =>
                          dispatch(demTemplatesActions.generateAiDemTemplate({ prompt, currentDesign }))
                        }
                        onUndo={(message) => {
                          dispatch(demTemplatesActions.undoLastAiDesign(message.id));
                          if (message.designSnapshotBefore && emailEditorRef.current?.editor) {
                            emailEditorRef.current.editor.loadDesign(message.designSnapshotBefore);
                          }
                        }}
                        onReset={() => dispatch(demTemplatesActions.resetAiConversation())}
                      />
                    </Col>
                  )}
                </Row>
                <Modal
                  isOpen={isImportModalOpen}
                  toggle={handleCloseImportModal}
                  size="lg"
                >
                  <ModalHeader toggle={handleCloseImportModal}>
                    {t('Import template from JSON')}
                  </ModalHeader>
                  <ModalBody>
                    <p className="text-muted mb-2">
                      {t('Paste the Unlayer JSON.')}
                    </p>
                    <Input
                      type="textarea"
                      rows="12"
                      value={importJson}
                      onChange={(event) => {
                        setImportJson(event.target.value);
                        if (importError) setImportError('');
                      }}
                      placeholder={t('Paste the template JSON here...')}
                    />
                    {importError ? (
                      <div className="text-danger mt-2">{importError}</div>
                    ) : null}
                  </ModalBody>
                  <ModalFooter>
                    <Button
                      color="secondary"
                      outline
                      onClick={handleCloseImportModal}
                    >
                      {t('Cancel')}
                    </Button>
                    <Button color="primary" onClick={handleImportDesign}>
                      {t('Import')}
                    </Button>
                  </ModalFooter>
                </Modal>
                <Modal
                  isOpen={isExportModalOpen}
                  toggle={handleCloseExportModal}
                  size="lg"
                >
                  <ModalHeader toggle={handleCloseExportModal}>
                    {t('Export design JSON')}
                  </ModalHeader>
                  <ModalBody>
                    <p className="text-muted mb-2">
                      {t(
                        'Copy the design JSON and paste it into Codex or Claude Code.',
                      )}
                    </p>
                    <Input
                      type="textarea"
                      rows="12"
                      value={exportJson}
                      readOnly
                    />
                  </ModalBody>
                  <ModalFooter>
                    <Button
                      color="secondary"
                      outline
                      onClick={handleCloseExportModal}
                    >
                      {t('Close')}
                    </Button>
                    <Button
                      color="primary"
                      onClick={handleCopyExportJson}
                      disabled={!exportJson}
                    >
                      {t('Copy')}
                    </Button>
                  </ModalFooter>
                </Modal>
              </>
            )}
          </Form>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default Editor;
